import React, { FC, useCallback, useState } from 'react';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons/faAngleLeft';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons/faAngleRight';
import { faAnglesLeft } from '@fortawesome/free-solid-svg-icons/faAnglesLeft';
import { faAnglesRight } from '@fortawesome/free-solid-svg-icons/faAnglesRight';
import { Document, Page, pdfjs } from 'react-pdf';
import { scroller } from 'react-scroll';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';

import useResizeObserver from '@buyside/util/hooks/useResizeObserver';

import styles from './thesisStyles.scss';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
const options = { cMapUrl: '/cmaps/' };

const PdfViewer: FC<{ fileUrl: string }> = ({ fileUrl }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [viewType, setViewType] = useState<'page' | 'list'>('list');
  const [ref, rect] = useResizeObserver();

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
  };
  const firstPage = (): void => {
    setPageNumber(1);
  };
  const prevPage = (): void => {
    if (pageNumber === 1) return;
    setPageNumber(pageNumber => pageNumber - 1);
  };
  const nextPage = (): void => {
    if (pageNumber >= numPages) return;
    setPageNumber(pageNumber => pageNumber + 1);
  };
  const lastPage = (): void => {
    setPageNumber(numPages);
  };
  const handleChangePage = useCallback((changePage: () => void) => {
    changePage();
    scroller.scrollTo('pdf-viewer', {
      duration: 0,
      delay: 0,
      offset: -(65 + 45 + 15) // -(top navigation height + page navigator height + some padding)
    });
  }, []);

  const pageNumbers = Array.from({ length: numPages }, (_, i) => i + 1);

  return (
    <div id="pdf-viewer" ref={ref}>
      <div className={styles.viewTypeToggle}>
        <Button
          buttonType={BUTTON_TYPES.BUTTON}
          onClick={() => setViewType('page')}
          size={BUTTON_SIZES.TINY}
          type={viewType === 'page' ? BUTTON_TYPES.PRIMARY : BUTTON_TYPES.SECONDARY}
          text="Page View"
        />
        <Button
          buttonType={BUTTON_TYPES.BUTTON}
          onClick={() => setViewType('list')}
          size={BUTTON_SIZES.TINY}
          type={viewType === 'list' ? BUTTON_TYPES.PRIMARY : BUTTON_TYPES.SECONDARY}
          text="List View"
        />
      </div>
      <Document
        className={styles.pdfContainer}
        file={fileUrl}
        loading={<LoadingSpinner size={SIZES.LARGE} />}
        onContextMenu={e => e.preventDefault()}
        onLoadSuccess={onDocumentLoadSuccess}
        options={options}
      >
        {viewType === 'page' ? (
          <Page pageNumber={pageNumber} renderTextLayer={false} width={rect?.width || 800} />
        ) : null}
        {viewType === 'list'
          ? pageNumbers.map(pageNum => (
              <Page
                key={`page-${pageNum}`}
                pageNumber={pageNum}
                renderTextLayer={false}
                width={rect?.width || 800}
              />
            ))
          : null}
      </Document>
      {viewType === 'page' && numPages ? (
        <div className={styles.pdfButtons}>
          <div>
            <Button
              disabled={pageNumber === 1}
              faIcon={faAnglesLeft}
              onClick={() => handleChangePage(firstPage)}
              size={BUTTON_SIZES.SMALL}
            />
            <Button
              disabled={pageNumber === 1}
              faIcon={faAngleLeft}
              onClick={() => handleChangePage(prevPage)}
              size={BUTTON_SIZES.SMALL}
            />
          </div>
          <span>
            Page {pageNumber} of {numPages}
          </span>
          <div>
            <Button
              disabled={pageNumber === numPages}
              faIcon={faAngleRight}
              onClick={() => handleChangePage(nextPage)}
              size={BUTTON_SIZES.SMALL}
            />
            <Button
              disabled={pageNumber === numPages}
              faIcon={faAnglesRight}
              onClick={() => handleChangePage(lastPage)}
              size={BUTTON_SIZES.SMALL}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PdfViewer;
